import type * as ClassTypes from './types/class';
import {
    initAlternativeProgressPopup,
    initAnimateNumbers,
    initAnimation,
    initApartmentLayoutSlider,
    initArchitectureSlider,
    initBannerSlider,
    initCallbackPopup,
    initCardsCarousel,
    initContentCarousel,
    initContentSlider,
    initHeader,
    initPageLoader,
    initPopup,
    initPostMessagePopup,
    initRainAnimation,
    initShare,
    initSimilarFlatsSlider,
    initStickySlider,
    initTabs,
    initVideoPlayer
} from './common';
import type {IDynamicImport} from './types/utils';
import {initFlatTabs} from '@/components/flat-views';
import type {IObserver} from './types/observer';
import Observer from './observer';
import {Utils} from '@/common/scripts/utils';
import UTM from '@/common/scripts/utm';

const observer: IObserver = new Observer();

new UTM().init();

// Поведение шапки
const headerWrap = document.querySelector('.j-header') as HTMLElement;
const headerWrapper = headerWrap || document.querySelector('header') as HTMLElement;

if (headerWrap) {
    initHeader(headerWrap);
}

const utpElements = [...document.querySelectorAll('.j-utp')] as HTMLElement[];

const utpWithTabs = utpElements.filter((el: HTMLElement) => {
    return el.querySelector('.j-tabs');
});

if (utpElements.length) {
    if (utpWithTabs.length) {
        import(/* webpackChunkName: "tabs" */ '@/components/tabs')
            .then(({default: Tabs}: IDynamicImport) => {
                utpWithTabs.forEach((element: HTMLElement) => {
                    const tabs: ClassTypes.ITabs = new Tabs();

                    tabs.init({
                        elem          : element,
                        tabsParent    : '.j-tabs',
                        tabItem       : '.j-tabs__item',
                        contentsParent: '.j-tabs-content',
                        contentItem   : '.j-tabs-content__item'
                    });
                });
            });
    }

    import(/* webpackChunkName: "utp" */ '@/sections/utp')
        .then(({default: UTP}: IDynamicImport) => {
            utpElements.forEach((element: HTMLElement) => {
                const utp: ClassTypes.IUTP = new UTP({
                    wrapper: element
                });

                utp.init();
            });
        });
}

// Слайдеры
const bannerSliders = [...document.querySelectorAll('.j-banner-slider')] as HTMLElement[];

if (bannerSliders.length) {
    bannerSliders.forEach((wrapper: HTMLElement) => {
        initBannerSlider(wrapper);
    });
}

const contentSliders = [...document.querySelectorAll('.j-content-slider')] as HTMLElement[];

if (contentSliders.length) {
    contentSliders.forEach((wrapper: HTMLElement) => {
        initContentSlider(wrapper);
    });
}

const contentCarousels = [...document.querySelectorAll('.j-content-carousel')] as HTMLElement[];

if (contentCarousels.length) {
    contentCarousels.forEach((wrapper: HTMLElement) => {
        initContentCarousel(wrapper);
    });
}

const purchaseMethodsCarousels =
    [...document.querySelectorAll('.j-purchase-methods-features-carousel')] as HTMLElement[];

if (purchaseMethodsCarousels.length) {
    purchaseMethodsCarousels.forEach((wrapper: HTMLElement) => {
        const slidesCount = wrapper.querySelectorAll('.j-content-carousel-item').length;
        const shouldEnableSlider = slidesCount > 3;

        wrapper.classList.toggle('slider-disabled', !shouldEnableSlider);

        if (!shouldEnableSlider) {
            return;
        }

        initContentCarousel(wrapper, {
            breakpoints: {
                360: {
                    slidesPerView: 1.01
                },
                768: {
                    slidesPerView: 1.06
                },
                1280: {
                    slidesPerView: 3
                }
            }
        });
    });
}

const architectureSliders = [...document.querySelectorAll('.j-architecture-slider')] as HTMLElement[];

if (architectureSliders.length) {
    architectureSliders.forEach((wrapper: HTMLElement) => {
        initArchitectureSlider(wrapper);
    });
}

const cardsCarousel = [...document.querySelectorAll('.j-cards-carousel-wrapper')] as HTMLElement[];

if (cardsCarousel.length) {
    cardsCarousel.forEach((wrapper: HTMLElement) => {
        initCardsCarousel(wrapper);
    });
}

const apartmentLayoutSlider = [...document.querySelectorAll('.j-apartment-layout-item')] as HTMLElement[];

if (apartmentLayoutSlider.length) {
    apartmentLayoutSlider.forEach((wrapper: HTMLElement) => {
        initApartmentLayoutSlider(wrapper);
    });
}

// Валидация форм
const formValidateForms = [...document.querySelectorAll('form[data-validate]')] as HTMLElement[];

if (formValidateForms.length) {
    // @ts-ignore
    import(/* webpackChunkName: "validation" */ '@/components/form/validation')
        .then(({default: Validation}: IDynamicImport) => {
            const validation: ClassTypes.IValidation = new Validation();

            validation.update();
        });
}

// Инициализация форм
const formElements = [...document.querySelectorAll('.j-form')] as HTMLElement[];

if (formElements.length) {
    // @ts-ignore
    import(/* webpackChunkName: "form" */ '@/components/form')
        .then(({default: Form}: IDynamicImport) => {
            formElements.forEach((element: HTMLElement) => {
                const form: ClassTypes.IForm = new Form();

                form.init({
                    target        : element,
                    successMessage: true
                });
            });
        });
}

// карта в секции контактов
const contactsMap = document.querySelector('.j-contacts-map') as HTMLElement;

if (contactsMap) {
    import(/* webpackChunkName: "contacts-section" */ '@/sections/contacts')
        .then(({default: initContactsMap}: IDynamicImport) => {
            initContactsMap(contactsMap);
        });
}

const locationMap = document.querySelector('.j-location-map') as HTMLElement;

if (locationMap) {
    import(/* webpackChunkName: "location-section" */ '@/sections/location')
        .then(({default: initLocationMap}: IDynamicImport) => {
            initLocationMap(locationMap);
        });
}

const similarFlatsSliders = [...document.querySelectorAll('.j-similar-flats')] as HTMLElement[];

if (similarFlatsSliders.length) {
    similarFlatsSliders.forEach((wrapper: HTMLElement) => {
        initSimilarFlatsSlider(wrapper);
    });
}

// Labels (overflow badges с тултипом)
const labelsElements = [...document.querySelectorAll('.j-labels')] as HTMLElement[];

if (labelsElements.length) {
    import(/* webpackChunkName: "labels" */ '@/components/labels')
        .then(({default: Labels}: IDynamicImport) => {
            labelsElements.forEach((element: HTMLElement) => {
                const labels: ClassTypes.ILabels = new Labels();

                labels.init(element);
            });
        });
}

// Табы карточки квартиры
const flatViewsElements = [...document.querySelectorAll('.j-flat-views__tabs')] as HTMLElement[];

if (flatViewsElements.length) {
    flatViewsElements.forEach((element: HTMLElement) => {
        initFlatTabs(element);
    });
}

// Табы секции "Как купить"
const howToBuyElements = [...document.querySelectorAll('.j-how-to-buy')] as HTMLElement[];

if (howToBuyElements.length) {
    import(/* webpackChunkName: "tabs" */ '@/components/tabs')
        .then(({default: Tabs}: IDynamicImport) => {
            howToBuyElements.forEach((element: HTMLElement) => {
                const tabs: ClassTypes.ITabs = new Tabs();

                tabs.init({
                    elem          : element,
                    tabsParent    : '.j-tabs',
                    tabItem       : '.j-tabs__item',
                    contentsParent: '.j-tabs-content',
                    contentItem   : '.j-tabs-content__item'
                });
            });
        });
}

// Sticky slider
const stickySliders = [...document.querySelectorAll('.j-scroll-section')] as HTMLElement[];

stickySliders.forEach((wrapper: HTMLElement) => {
    initStickySlider(wrapper);
});

// j-visual-frame
const visualIframes = [...document.querySelectorAll('.j-visual-frame')];

if (visualIframes.length) {
    initPostMessagePopup();
}

// Ипотечный калькулятор
const mortgageCalculator = document.querySelector('.j-mortgage-calculator') as HTMLElement;

if (mortgageCalculator) {
    import(/* webpackChunkName: "mortgage-calculator" */ '@/components/mortgage-calculator')
        .then(({default: MortgageCalculator}: IDynamicImport) => {
            const calculator: ClassTypes.IMortgageCalculator = new MortgageCalculator(mortgageCalculator);

            calculator.init();

            // Подстановка минимального месячного платежа в блок карточки квартиры
            const flatMortgage = document.querySelector('.j-flat-mortgage') as HTMLElement | null;
            const monthlyPayment = mortgageCalculator.dataset['minMonthly'];

            if (flatMortgage) {
                import(/* webpackChunkName: "price-monthly-payment" */ '@/components/price')
                    .then(({default: Price}: IDynamicImport) => {
                        const priceMonthlyPayment = new Price({
                            element     : flatMortgage,
                            payment     : monthlyPayment,
                            textSelector: '.j-flat-mortgage__text',
                            href        : mortgageCalculator.closest('section')?.id || null
                        });

                        priceMonthlyPayment.init();
                    });
            }
        });
}

const headerFavorite = document.querySelector('.j-header__favorites-counter');

if (headerFavorite) {
    import(/* webpackChunkName: "favorites" */ '@/components/favorite')
        .then(({default: changeHeaderFavorite}: IDynamicImport) => {
            changeHeaderFavorite(headerFavorite);
        });
}

const favoriteButtons = document.querySelectorAll<HTMLElement>('.j-favorite');

if (favoriteButtons.length) {
    import(/* webpackChunkName: "flat-card" */ '@/sections/flat-card')
        .then(({default: initFlatPageButtons}: IDynamicImport) => {
            favoriteButtons.forEach((favoriteButton: HTMLElement) => {
                initFlatPageButtons(favoriteButton);
            });
        });
}

// Шеринг
const shareElements = [...document.querySelectorAll('.j-share')] as HTMLElement[];

if (shareElements.length) {
    initShare(shareElements);
}

// Cкролл по якорям
const anchors = [...document.querySelectorAll('.j-anchor')] as HTMLElement[];

if (anchors.length) {
    import(/* webpackChunkName: "scrollBy" */ '@/components/scrollBy')
        .then(({default: ScrollBy}: IDynamicImport) => {
            const scrollBy: ClassTypes.IScrollBy = new ScrollBy();
            const CLASS_ACTIVE = 'is-active';

            scrollBy.init({
                takeElements: [headerWrapper],
                anchors
            });

            const setActiveAnchor = (id: string): void => {
                anchors.forEach((item: HTMLElement) => {
                    const href = item.getAttribute('href');

                    item.classList.remove(CLASS_ACTIVE);

                    if (href === id || href === `/${id}`) {
                        item.classList.add(CLASS_ACTIVE);
                    }
                });
            };

            const hash = window.location.hash;

            if (hash.length) {
                setActiveAnchor(hash);
            }

            observer.subscribe('anchorChangeSection', (id: string) => {
                setActiveAnchor(id);
            });
        });
}

const visualOffset = document.querySelector('.j-visual-offset') as HTMLElement;
const setVisualOffset = (): void => {
    document.documentElement.style.setProperty('--visual-offset', `${visualOffset.offsetHeight}px`);
};

if (visualOffset) {
    setVisualOffset();

    window.addEventListener('resize', Utils.debounce(() => {
        setVisualOffset();
    }, 10));
}

const popupSelectors = [...document.querySelectorAll('.j-popup')] as HTMLElement[];

if (popupSelectors.length) {
    initPopup(popupSelectors, {removeDelay: 300});
}

// Анимация дождя
const rainAnimations = [...document.querySelectorAll('.j-rain-animation')] as HTMLElement[];

if (rainAnimations.length) {
    rainAnimations.forEach((wrapper: HTMLElement) => {
        initRainAnimation(wrapper);
    });
}

// Загрузчик страницы
const pageLoader = document.querySelector('.j-page-loader') as HTMLElement;

if (pageLoader) {
    initPageLoader(pageLoader);
}

// Отдельный селектор для попапов обратных звонков
const popupFacadeLinks = [...document.querySelectorAll('.j-popup-callback:not([data-init])')] as HTMLElement[];

if (popupFacadeLinks.length) {
    initCallbackPopup(popupFacadeLinks);

    popupFacadeLinks.forEach((button: HTMLElement) => {
        button.dataset['init'] = 'true';
    });
}

// Video
const videoPlayers = [...document.querySelectorAll('.j-video')] as HTMLElement[];

if (videoPlayers.length) {
    videoPlayers.forEach((videoPlayer: HTMLElement) => {
        initVideoPlayer(videoPlayer);
    });
}

// Уведомление об использовании cookies
const cookiesWrapper = document.querySelector('.j-cookies-notice') as HTMLElement;

if (cookiesWrapper) {
    import(/* webpackChunkName: "cookies-notice" */ '@/components/cookies-notice')
        .then(({default: CookiesNotice}: IDynamicImport) => {
            const cookiesNotice = new CookiesNotice();

            cookiesNotice.init(cookiesWrapper);
        });
}

// Табы
const tabs = [...document.querySelectorAll<HTMLElement>('.j-tabs-wrap')];

tabs.forEach((elem: HTMLElement) => {
    initTabs(elem);
});

const upButton = document.querySelector<HTMLElement>('.j-up-button');

if (upButton) {
    upButton.addEventListener('click', () => {
        window.scrollTo({
            top     : 0,
            behavior: 'smooth'
        });
    });
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
        document.dispatchEvent(new Event('externalReady'));
    });
}

const progressAPP = document.getElementById('progress');

if (progressAPP) {
    // @ts-ignore
    document.addEventListener('progressData', (data: CustomEvent) => {
        initAlternativeProgressPopup(data.detail.cards);
    });
}

const animateNumbers = [...document.querySelectorAll<HTMLElement>('.j-animate-numbers')];

animateNumbers.forEach((elem: HTMLElement) => {
    initAnimateNumbers(elem);
});

// Анимация
initAnimation();
